"use client";

import Link from "next/link";
import { Container } from "@/components/Container";
import { MaskReveal, RevealGroup, RevealItem, RevealImage } from "@/components/Motion";
import { featuredCities } from "@/lib/home";

export type HomeCity = { slug: string; name: string; image: string };

/**
 * The homepage city grid.
 *
 * Cities come from the database, so adding, renaming, unpublishing or
 * reordering one in /admin/locations changes this section too — it used to be
 * a hardcoded list of eight, which is why Bhubaneswar never appeared here
 * while the copy above it claimed nine. `featuredCities` stays as the
 * fail-soft fallback for when the DB is unreachable.
 *
 * The heading and the sentence are editable in /admin/pages ("Home"). Left
 * alone, the sentence counts the cities itself, so it can never drift from
 * the grid below it.
 */
export function Locations({
  cities,
  c = {},
}: {
  cities?: HomeCity[];
  c?: Record<string, string>;
}) {
  const list = cities?.length ? cities : featuredCities.map((f) => ({
    slug: f.slug, name: f.name, image: f.image,
  }));
  const title = c["locations.title"]?.trim() || "Locations";
  const intro =
    c["locations.intro"]?.trim() ||
    `With a strong presence across ${list.length} major cities in India, ` +
      "Table Space offers workspace solutions for all your evolving enterprise needs.";

  return (
    <section className="border-t border-brand-line bg-brand-paper text-brand-ink">
      <Container className="py-28 md:py-40">
        <div className="max-w-3xl">
          <h2 data-cms-key="locations.title" className="t-display text-brand-ink">
            <MaskReveal lines={[<span key="a" className="text-brand-accent">{title}</span>]} />
          </h2>
          <p
            data-cms-key="locations.intro"
            className="mt-3 text-base font-normal leading-relaxed text-brand-slate"
          >
            {intro}
          </p>
        </div>

        <RevealGroup
          className="mt-14 grid grid-cols-2 gap-x-5 gap-y-8 md:mt-20 md:grid-cols-4"
          stagger={0.06}
        >
          {list.map((city) => (
            <RevealItem key={city.slug}>
              <Link href={`/locations/${city.slug}`} className="group block">
                <div className="aspect-square w-full overflow-hidden rounded-2xl ring-1 ring-brand-line">
                  {city.image && (
                    <RevealImage
                      src={city.image}
                      alt={`Managed office space in ${city.name} — Table Space`}
                      hover
                      className="h-full w-full object-cover [filter:contrast(1.03)_saturate(1.04)]"
                    />
                  )}
                </div>
                <div className="mt-4">
                  <h3 className="text-base font-medium text-brand-ink transition-colors duration-300 group-hover:text-brand-accent">
                    {city.name}
                  </h3>
                </div>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>

        <div className="mt-12">
          <Link href="/contact-us" className="btn btn-secondary btn-sm">
            Enquire now
          </Link>
        </div>
      </Container>
    </section>
  );
}
